import { useEffect, useRef } from 'react';
import { botStatus } from '@src/services/claw/api';
import useClawStatus, { ClawStatus, PendingStep } from '@src/stores/useClawStatus';

export enum BotStatus {
  NOT_CREATED = 'NOT_CREATED',
  USER_CONTEXT_PREPARING = 'USER_CONTEXT_PREPARING',
  SANDBOX_CREATING = 'SANDBOX_CREATING',
  ENV_CONFIGURING = 'ENV_CONFIGURING',
  READY = 'READY',
  ERROR = 'ERROR',
}

/** OPENING 中各子状态对应的进度步骤 */
const BOT_STATUS_TO_PENDING_STEP: Partial<Record<BotStatus, PendingStep>> = {
  [BotStatus.NOT_CREATED]: PendingStep.PREPARING,
  [BotStatus.USER_CONTEXT_PREPARING]: PendingStep.LOADING_AI,
  [BotStatus.SANDBOX_CREATING]: PendingStep.INSTALLING,
  [BotStatus.ENV_CONFIGURING]: PendingStep.STARTING,
  [BotStatus.READY]: PendingStep.COMPLETE,
};

const OPENING_STATUSES: BotStatus[] = [
  BotStatus.USER_CONTEXT_PREPARING,
  BotStatus.SANDBOX_CREATING,
  BotStatus.ENV_CONFIGURING,
];

const POLL_INTERVAL_MS = 3000;

const mapBotStatus = (status?: BotStatus): ClawStatus => {
  if (!status || status === BotStatus.NOT_CREATED) return ClawStatus.NOT_OPEN;
  if (OPENING_STATUSES.includes(status)) return ClawStatus.OPENING;
  if (status === BotStatus.READY) return ClawStatus.OPENED;
  if (status === BotStatus.ERROR) return ClawStatus.ERROR;
  return ClawStatus.NOT_OPEN;
};

/**
 * 进入页面时查询 Bot 状态，写入 useClawStatus store。
 * - 首次查询完成后解除 checking 遮罩。
 * - 若状态为 OPENING，自动启动轮询（每 3s 一次），并同步 pendingStep。
 * - 轮询在状态变为终态（OPENED / NOT_OPEN / ERROR）或组件卸载时停止。
 */
export interface UseClawBotStatusOptions {
  /** 是否在 OPENING 状态下启用轮询，默认 true */
  polling?: boolean;
}

export function useClawBotStatus({ polling = true }: UseClawBotStatusOptions = {}) {
  const { setStatus, setChecking, setPendingStep, init } = useClawStatus();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const stopTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const applyStatus = (rawStatus?: BotStatus) => {
      const clawStatus = mapBotStatus(rawStatus);
      setStatus(clawStatus);

      const step = rawStatus ? BOT_STATUS_TO_PENDING_STEP[rawStatus] : undefined;
      if (step !== undefined) setPendingStep(step);

      return clawStatus;
    };

    const poll = () => {
      botStatus()
        .then((res) => {
          if (cancelled) return;
          const clawStatus = applyStatus(res.status as BotStatus | undefined);
          // 仍在 OPENING 中且轮询开关开启 → 继续轮询
          if (polling && (clawStatus === ClawStatus.OPENING || init)) {
            timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (!cancelled) setStatus(ClawStatus.NOT_OPEN);
        });
    };

    setChecking(true);

    botStatus()
      .then((res) => {
        if (cancelled) return;
        const clawStatus = applyStatus(res.status as BotStatus | undefined);
        // 首次查询后解除 checking，如果还在 OPENING 且轮询开关开启则启动轮询
        setChecking(false);
        if (polling && (clawStatus === ClawStatus.OPENING || init)) {
          timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus(ClawStatus.NOT_OPEN);
          setChecking(false);
        }
      });

    return () => {
      cancelled = true;
      stopTimer();
    };
  }, [setStatus, setChecking, setPendingStep]);
}
